import axios from "axios";
import apiClient, { assertApiUrlConfigured } from "./apiClient";
import { jwtDecode } from "jwt-decode";
import { clearModuleAccessCache, prefetchTenantModuleAccess } from "../utils/moduleAccessStore";
import { clearTenantIndustryCache } from "./TenantService";
import {
  checkTenantSubscriptionActive,
  clearSubscriptionBlocked,
} from "../utils/subscriptionAccessStore";
import {
  clearStoredAccessToken,
  getRefreshInFlight,
  getStoredAccessToken,
  setRefreshInFlight,
  setStoredAccessToken,
} from "./accessTokenStore";
import { unwrapEnvelopeData } from "./apiHelpers";
import { isAuthEntryRequest, isPublicSessionRestoreSkipPath } from "./authApiPaths";
import { isChannelPartnerRole, isSuperAdminRole } from "../../routes/roles";
import { normalizeEntityId, isValidGuid } from "../utils/guidValidation";
import {
  clearSessionTimeoutPending,
  isSessionTimeoutPending,
  requestSessionTimeout,
} from "./sessionTimeoutStore";

const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";

function isEmptyGuid(value) {
  return String(value || "").toLowerCase() === EMPTY_GUID;
}

function isLegacyNonGuidId(value) {
  return value != null && value !== "" && !isValidGuid(value) && !isEmptyGuid(value);
}

function authPayload(response) {
  return unwrapEnvelopeData(response);
}

function extractAccessToken(data) {
  if (!data || typeof data === "string") return null;
  return (
    data.accessToken
    ?? data.AccessToken
    ?? data.token
    ?? data.Token
    ?? data.newAccessToken
    ?? data.NewAccessToken
    ?? null
  );
}

let sessionRestorePromise = null;

const LOGGED_OUT_FLAG = 'nf_session_logged_out';
/** Set after a successful login/refresh so bootstrap does not hit RefreshTokens on first visit. */
const SESSION_HINT_KEY = 'nf_session_hint';

const AuthService = {
  getToken() {
    return getStoredAccessToken();
  },

  setToken(token) {
    setStoredAccessToken(token);
    if (token) {
      apiClient.defaults.headers.common.Authorization = `Bearer ${token}`;
    } else {
      delete apiClient.defaults.headers.common.Authorization;
    }
  },

  removeToken() {
    clearStoredAccessToken();
    delete apiClient.defaults.headers.common.Authorization;
  },

  getUser() {
    const token = this.getToken();
    if (!token) return null;
    try {
      const decoded = jwtDecode(token);
      
      // Validate GUID format for entity ID claims (spec 009)
      // If validation fails, token is from pre-migration or malformed
      const userId = decoded.UserId ?? decoded.userId;
      const clientId = decoded.ClientId ?? decoded.clientId;

      // Empty GUID is invalid identity but must not hide ClientId/UserRoleId.
      if (isLegacyNonGuidId(userId)) {
        console.error('[AuthService] Token userId is not a valid GUID. Token may be pre-migration.');
        return null;
      }

      if (isLegacyNonGuidId(clientId)) {
        console.error('[AuthService] Token clientId is not a valid GUID. Token may be pre-migration.');
        return null;
      }

      return decoded;
    } catch (error) {
      console.error("Error decoding token:", error);
      return null;
    }
  },

  buildUserDetailFromToken(token) {
    const user = jwtDecode(token);
    
    // Extract and validate GUID claims (spec 009 - frontend GUID migration)
    const userId = user.UserId ?? user.userId;
    const clientId = user.ClientId ?? user.clientId;
    const userRoleId = user.UserRoleId ?? user.userRoleId;
    const partnerBuilderId = user.PartnerBuilderId ?? user.partnerBuilderId;
    
    // Validate that critical entity IDs are in GUID format
    // This catches pre-migration tokens or incorrectly formatted claims
    if (isLegacyNonGuidId(userId)) {
      console.error('[AuthService] Token contains non-GUID userId:', userId);
      throw new Error('Invalid token format: userId must be a GUID. Please log in again.');
    }

    if (isLegacyNonGuidId(clientId)) {
      console.error('[AuthService] Token contains non-GUID clientId:', clientId);
      throw new Error('Invalid token format: clientId must be a GUID. Please log in again.');
    }
    
    if (userRoleId && !isValidGuid(userRoleId)) {
      console.warn('[AuthService] Token contains non-GUID userRoleId:', userRoleId);
      // UserRoleId warning only - some system roles might have special format
    }
    
    return {
      username: user.UserName,
      UserEmail: user.UserEmail,
      UserId: userId,
      UserRoleId: userRoleId,
      ClientId: clientId,
      PartnerBuilderId: partnerBuilderId,
    };
  },

  setSessionFromAccessToken(accessToken) {
    if (!accessToken) return;
    sessionStorage.removeItem(LOGGED_OUT_FLAG);
    localStorage.setItem(SESSION_HINT_KEY, '1');
    setStoredAccessToken(accessToken);
    apiClient.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("token");
    localStorage.removeItem("user");
  },

  /** Restore session on page load via httpOnly refresh cookie. */
  async restoreSession() {
    if (sessionRestorePromise) return sessionRestorePromise;
    sessionRestorePromise = this._restoreSessionOnce();
    return sessionRestorePromise;
  },

  async _restoreSessionOnce() {
    if (sessionStorage.getItem(LOGGED_OUT_FLAG) === '1') {
      this.removeToken();
      localStorage.removeItem(SESSION_HINT_KEY);
      return false;
    }

    if (isPublicSessionRestoreSkipPath()) {
      return false;
    }

    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("token");
    localStorage.removeItem("user");

    if (this.getToken() && !this.isTokenExpired(this.getToken())) {
      return true;
    }

    // No prior login on this browser — skip refresh (avoids expected 401 noise on /login).
    if (!localStorage.getItem(SESSION_HINT_KEY)) {
      return false;
    }

    try {
      const response = await this.refreshTokens();
      const payload = authPayload(response);
      const token = payload?.newAccessToken ?? extractAccessToken(payload);
      if (!token) {
        localStorage.removeItem(SESSION_HINT_KEY);
        return false;
      }
      this.setSessionFromAccessToken(token);
      if (!this.isUsableAgencySession()) {
        this.removeToken();
        localStorage.removeItem(SESSION_HINT_KEY);
        return false;
      }
      if (this.getLoggedInClientId()) {
        await prefetchTenantModuleAccess().catch(() => {});
      }
      await checkTenantSubscriptionActive().catch(() => true);
      return true;
    } catch {
      this.removeToken();
      localStorage.removeItem(SESSION_HINT_KEY);
      return false;
    }
  },

  async login(username, password, rememberMe, navigate) {
    assertApiUrlConfigured();
    const loginPayload = { email: username, password };
    try {
      const response = await apiClient.post(`/shared/auth/login`, loginPayload, {
        withCredentials: true,
        skipAuthRefresh: true,
      });
      let token = extractAccessToken(authPayload(response));

      if (!token) {
        try {
          const refreshResponse = await this.refreshTokens();
          token = extractAccessToken(authPayload(refreshResponse));
        } catch {
          /* refresh cookie may be unavailable on first login */
        }
      }

      if (!token) {
        const err = new Error("No access token returned");
        err.displayMessage = "Sign-in succeeded but the server did not return a session token. Restart the API and try again.";
        throw err;
      }

      this.setSessionFromAccessToken(token);
      if (!this.isUsableAgencySession()) {
        this.removeToken();
        localStorage.removeItem(SESSION_HINT_KEY);
        const err = new Error("Invalid session identity");
        err.displayMessage = "Sign-in succeeded but the account identity is invalid. Contact your administrator.";
        throw err;
      }
      const cid = this.getLoggedInClientId();
      if (cid) {
        await prefetchTenantModuleAccess().catch(() => {});
        const { fetchTenantIndustry } = await import('./TenantService');
        await fetchTenantIndustry(cid).catch(() => {});
      }
      const subActive = await checkTenantSubscriptionActive().catch(() => true);

      if (rememberMe) {
        this.setRememberedCredentials(username);
      } else {
        this.clearRememberedCredentials();
      }

      const roleId = this.getLoggedInRoleId();
      const home = isChannelPartnerRole(roleId) ? "/partner/dashboard" : "/dashboard";
      navigate(subActive ? home : "/subscription-inactive");
    } catch (err) {
      const data = err?.response?.data;
      const apiMsg = typeof data === "string"
        ? data
        : (data?.error?.message ?? data?.message ?? data?.error ?? data?.title);
      if (apiMsg) {
        err.displayMessage = apiMsg;
      }
      console.error("Login failed", err);
      throw err;
    }
  },

  async performLogout(navigate) {
    sessionStorage.setItem(LOGGED_OUT_FLAG, '1');
    sessionRestorePromise = null;

    try {
      await apiClient.post(`/shared/auth/logout-session`, {}, { withCredentials: true });
    } catch (error) {
      console.warn("Server session logout failed; clearing local session anyway.", error);
    }

    const userId = this.getLoggedInUserId();
    const token = this.getToken();
    try {
      if (userId && token) {
        await apiClient.put(`/shared/auth/logout/${userId}`, null, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${token}` },
        });
      }
    } catch (error) {
      console.warn("Authenticated logout failed; local session already cleared.", error);
    }

    clearModuleAccessCache();
    clearTenantIndustryCache();
    clearSubscriptionBlocked();
    clearSessionTimeoutPending();
    this.removeToken();
    localStorage.removeItem(SESSION_HINT_KEY);
    localStorage.removeItem("hasShownPopup");
    localStorage.removeItem("lastActivityTime");

    if (typeof navigate === "function") {
      navigate("/login", { replace: true });
    } else if (typeof window !== "undefined") {
      window.location.assign(`${window.location.origin}/login`);
    }
  },

  logout() {
    this.performLogout(null);
  },

  /** JWT-derived profile (not tamperable localStorage). */
  getCurrentUser() {
    const token = this.getToken();
    if (!token || this.isTokenExpired(token)) return null;
    try {
      return this.buildUserDetailFromToken(token);
    } catch {
      return null;
    }
  },

  isAuthenticated() {
    const token = this.getToken();
    if (!token) return false;
    if (!this.isTokenExpired(token)) return true;
    return isSessionTimeoutPending();
  },

  _jwtClaims() {
    return this.getUser() || {};
  },

  getLoggedInUserId() {
    const claims = this._jwtClaims();
    return normalizeEntityId(claims.UserId ?? claims.userId);
  },

  getLoggedInRoleId() {
    const claims = this._jwtClaims();
    const role = claims.UserRoleId ?? claims.userRoleId;
    return role ?? null;
  },

  getLoggedInClientId() {
    const claims = this._jwtClaims();
    return normalizeEntityId(claims.ClientId ?? claims.clientId);
  },

  isUsableAgencySession() {
    const userId = this.getLoggedInUserId();
    if (!isValidGuid(userId)) return false;
    if (isSuperAdminRole(this.getLoggedInRoleId())) return true;
    return isValidGuid(this.getLoggedInClientId());
  },

  getPartnerBuilderId() {
    const claims = this._jwtClaims();
    const id = claims.PartnerBuilderId ?? claims.partnerBuilderId;
    if (id == null) return null;
    return normalizeEntityId(id) ?? String(id);
  },

  isChannelPartner() {
    return isChannelPartnerRole(this.getLoggedInRoleId());
  },

  /**
   * Employees always use their JWT user id.
   * Admin/SuperAdmin may pass ?id= for team views (backend enforces tenant).
   */
  resolveScopedUserId(queryUserId) {
    const jwtId = this.getLoggedInUserId();
    if (!jwtId) return null;
    const roleId = this.getLoggedInRoleId();
    // Employee role GUID - employees always use their JWT user ID
    const employeeRoleId = 'A26DBE37-89F6-5FA9-AE92-0CE22BD8CD0D';
    if (String(roleId || '').toUpperCase() === employeeRoleId.toUpperCase()) return jwtId;
    if (queryUserId == null || queryUserId === "") return jwtId;
    const normalized = normalizeEntityId(queryUserId);
    return normalized ?? jwtId;
  },

  async getLastLoginTime() {
    const userId = this.getLoggedInUserId();
    if (!userId) throw new Error("User ID is not available");
    try {
      const response = await apiClient.get(`/User/lastlogintime/${userId}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching last login time", error);
      throw error;
    }
  },

  setRememberedCredentials(email) {
    localStorage.setItem("email", email);
    localStorage.removeItem("password");
  },

  getRememberedCredentials() {
    return { email: localStorage.getItem("email"), password: "" };
  },

  clearRememberedCredentials() {
    localStorage.removeItem("email");
    localStorage.removeItem("password");
  },

  handleLogout() {
    this.performLogout(null);
  },

  async refreshTokens() {
    if (sessionStorage.getItem(LOGGED_OUT_FLAG) === '1') {
      return Promise.reject(new Error('Session logged out'));
    }

    let inFlight = getRefreshInFlight();
    if (inFlight) return inFlight;

    inFlight = apiClient.post(
      `/shared/auth/refresh`,
      {},
      {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
        skipAuthRefresh: true,
      }
    );
    setRefreshInFlight(inFlight);

    try {
      return await inFlight;
    } finally {
      setRefreshInFlight(null);
    }
  },

  isTokenExpired(token) {
    if (!token) return false;
    try {
      const decodedToken = jwtDecode(token);
      return decodedToken.exp < Date.now() / 1000;
    } catch {
      return true;
    }
  },

  getTokenExpiryMs(token = this.getToken()) {
    if (!token) return null;
    try {
      const decodedToken = jwtDecode(token);
      return decodedToken.exp ? decodedToken.exp * 1000 : null;
    } catch {
      return null;
    }
  },

  async extendSession() {
    const response = await this.refreshTokens();
    const payload = authPayload(response);
    const token = extractAccessToken(payload);
    if (!token) {
      throw new Error("No access token returned");
    }
    this.setSessionFromAccessToken(token);
    clearSessionTimeoutPending();
    return token;
  },

  notifySessionTimeout(reason = "token-expired") {
    requestSessionTimeout(reason);
  },

  prepareAuthenticatedRequest(config) {
    const url = config?.url ?? "";
    if (config?.skipAuthRefresh || isAuthEntryRequest(url)) {
      const token = this.getToken();
      if (token && this.isTokenExpired(token)) {
        this.removeToken();
      }
      return config;
    }

    const token = this.getToken();
    if (!token) return config;
    if (this.isTokenExpired(token)) {
      this.notifySessionTimeout("token-expired");
      return Promise.reject(new axios.Cancel("Session expired"));
    }
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
    config.withCredentials = true;
    return config;
  },

  getUserImageById: async (userId) => {
    const response = await apiClient.get(`/User/GetUserImage/${userId}`);
    return response.data;
  },

  getAgencyLogoByClientId: async (clientId) => {
    const response = await apiClient.get(`/Client/GetAgencyLogoByClientId/${clientId}`);
    return response.data;
  },

  getAgencyLogoAndNameByClientId: async (clientId) => {
    const response = await apiClient.get(`/Client/GetAgencyLogoAndNameByClientId/${clientId}`);
    const data = response.data;
    return {
      agencyName: data?.agencyName ?? data?.AgencyName ?? "",
      agencyLogo: data?.agencyLogo ?? data?.AgencyLogo ?? "",
    };
  },
};

export default AuthService;
